/**
 * Level-Based Stat Scaling System
 *
 * Scales combat stats by level so higher-level pets are stronger.
 * Evolution stages are cosmetic only — level is the single scaling factor.
 *
 * SCALING RATES:
 *   Offensive (+1/level): attack, magic damage*, magic heal, special effect value*
 *   Defensive (+HP_PER_LEVEL/level): HP, on top of the species' own `baseHp` pool
 *   Recover (+0.1/level, floored): recover heal
 *   Shield (+1 per SHIELD_LEVELS_PER_BONUS levels): shield specialValue on attack/magic/dodge
 *
 *   A pool wide enough to keep fights multi-turn (so single hits can't one-shot)
 *   is each species' own `baseHp` in `speciesConfig.ts`, not a global dial;
 *   offense and defense then grow at the same +1/level,
 *   which is shape-preserving only because COMBAT_MAX_LEVEL bounds the curve at
 *   20 — see {@link HP_PER_LEVEL}, which carries the measurement and the reason
 *   the alternative (a faster bar) was measured and rejected.
 *   Shield grows slower (1 per 3 levels) since it stacks with the HP pool.
 *
 * MAGIC SCALING RULE (prevents double-scaling):
 *   - Species WITH scalable special (dot/drainlife/execute): special value scales, magic damage does NOT
 *   - Species WITHOUT scalable special (stun/wound/block/none, incl. shield): magic damage scales normally
 *   Shield's specialValue scales independently of this rule (see SHIELD_LEVELS_PER_BONUS) — magic
 *   damage for shield species still scales normally since shield is a defensive, not offensive, stat.
 *
 * USAGE:
 *   scaleCombatConfig(combat, level) → new SpeciesCombatConfig with all values scaled.
 *   Pass the result to the battle engine. One call, no scattered scaling logic.
 */

import type { ActiveCombatSpecialEffect, CombatSkill, SpeciesCombatConfig } from './petModels.js';
import { COMBAT_SPECIAL_EFFECT, hasCombatSpecial } from './petModels.js';
import { LEVEL_GAP_DAMAGE_K, LEVEL_GAP_THRESHOLD } from './pvpConstants.js';

// ============================================================================
// Bonus Calculations
// ============================================================================

/** Offensive bonus: +1 per level above 1. Attack, magic damage, magic heal, special values. */
function offensiveBonus(level: number): number {
  return level <= 1 ? 0 : (level - 1);
}

/**
 * **HP PER LEVEL — 1, and it was briefly 2. The story is worth keeping, because the arithmetic
 * that justified 2 is real and the fix for it turned out to live somewhere else entirely.**
 *
 * The concern is genuine: a level adds +1 to attack and magic and +1 here, which reads as fair
 * and is not shape-preserving, because **the two increments are equal but the two BASES are
 * not.** In a cards battle the bar carries `CARDS_HP_MULTIPLIER` (x2) and the hit carries
 * `CARDS_DAMAGE_MULTIPLIER` (x1.1), so per level the bar gains +2 and the hit +1.1 — off a bar
 * that starts near 156 and a hit near 22. Each level therefore adds damage worth 55% of the HP
 * it adds to a fighter whose hit was 14% of a bar, and the ratio climbs from 14% toward
 * **1.1/2 = 55%** without ever settling.
 *
 * **It climbs forever only if the levels go on forever, and they no longer do.**
 * {@link COMBAT_MAX_LEVEL} is now the ceiling in every mode including the campaign, which used
 * to lift itself out of it, so the whole curve is 1-20 and the climb is bounded by the cap
 * instead of by this dial. Measured at the cap, on 5/10/15/20, `--battles 40 --grid-battles 8`,
 * seeds 1337/42 — the two settings side by side:
 *
 * | | at 2 | **at 1** |
 * |---|---|---|
 * | `20` top of the curve over the bottom (1.15-1.60x) | **1.10x ✗** | **1.25x ✓** |
 * | `20` the deck's top step at max level (1.10-1.50x) | 1.31x ✓ | **1.38x ✓** |
 * | `10` rounds · burst p99 (≤70%) | 8.7 · 54.4% ✓ | 8.3 · 58.7% ✓ |
 * | `13` winner's lowest bar (10-60%) | **61.9% ✗** | **60.1% ✗** |
 * | `11` least-used suit (≥15%) | **15.0% ✗** | 14.7% ✗ |
 * | `15` typical hit (15-33%) | 17.4% ✓ | 19.4% ✓ |
 * | score | 13/20 | **14/19** |
 *
 * **At 2, with the cap in place, levelling buys almost nothing** — a hit is 16.4% of a bar at
 * level 5 and 18.1% at 20, so criterion 20 fails its FLOOR and the twenty levels a player climbs
 * stop meaning anything. At 1 the same span reads 17.1% → 21.4%, a 1.25x rise, which is a
 * progression you can feel inside a band nothing leaves. **The cap and this dial were two answers
 * to one question, and the cap is the better one:** it fixes the endgame by construction and costs
 * no combat constant at all.
 *
 * Recorded rather than quietly reverted, because the trap is easy to fall into twice: the
 * arithmetic above says "raise HP per level" and it is correct about an UNBOUNDED curve. Check
 * where the curve ends before turning it. Tuning log Rows 47 and 48.
 */
export const HP_PER_LEVEL = 1;

/** Defensive bonus: +{@link HP_PER_LEVEL} HP per level above 1. */
function defensiveBonus(level: number): number {
  return level <= 1 ? 0 : (level - 1) * HP_PER_LEVEL;
}

/** Levels needed per +1 Shield value. Shield grows slower than other offense/defense stats. */
export const SHIELD_LEVELS_PER_BONUS = 3;

/** Shield bonus: +1 per SHIELD_LEVELS_PER_BONUS levels above 1. */
function shieldBonus(level: number): number {
  return level <= 1 ? 0 : Math.floor((level - 1) / SHIELD_LEVELS_PER_BONUS);
}

// ============================================================================
// Special Effect Classification
// ============================================================================

/** Effects with numeric values that scale with level. When present, magic damage does NOT scale. */
const SCALABLE_EFFECTS: ActiveCombatSpecialEffect[] = [
  COMBAT_SPECIAL_EFFECT.Dot,
  COMBAT_SPECIAL_EFFECT.DrainLife,
  COMBAT_SPECIAL_EFFECT.Execute,
];

/** Returns true if the species magic has a scalable special effect (dot/drainlife/execute). */
export function hasScalableSpecialEffect(combat: SpeciesCombatConfig): boolean {
  return hasCombatSpecial(combat.magic.special) && SCALABLE_EFFECTS.includes(combat.magic.special);
}

// ============================================================================
// Individual Stat Scalers (used by scaleCombatConfig and UI utilities)
// ============================================================================

/**
 * HP: a species' `baseHp` IS its level-1 pool, plus {@link HP_PER_LEVEL} per level.
 *
 * There used to be a global `HP_SCALE_MULTIPLIER` here (1.3, then 1.04) that every
 * species' `baseHp` was silently multiplied by. It was removed because the number in
 * `speciesConfig.ts` was then never the number the game used: a config reading 53 put
 * 55 HP on the board, and the gap moved under every species at once whenever the dial
 * moved. Balance reads and per-species tuning both had to remember to apply it, and the
 * player-facing surfaces had to print the formula rather than the value.
 *
 * `baseHp` in `speciesConfig.ts` is now the real level-1 pool and is the ONLY place HP
 * is set. Tune a species by editing its own `baseHp`; there is deliberately no global
 * dial to move them all together, because "they all moved and nobody decided which"
 * is what the multiplier made easy.
 *
 * The floor any HP edit must respect: combat damage (base + flat charge/element bonuses
 * + conditional specials) must not be able to delete an opponent in a single turn -- the
 * "one-hit kill" problem the multiplier was originally introduced to solve.
 */
export function calculateScaledHp(baseHp: number, level: number): number {
  return baseHp + defensiveBonus(level);
}

/** Attack damage: base + offensive bonus (+1/level) */
export function calculateScaledAttackDamage(baseDamage: number, level: number): number {
  return baseDamage + offensiveBonus(level);
}

/** Magic damage: +1/level if no scalable special, +0 if has scalable special (to avoid double-scaling) */
export function calculateScaledMagicDamage(combat: SpeciesCombatConfig, level: number): number {
  if (hasScalableSpecialEffect(combat)) return combat.magic.damage;
  return combat.magic.damage + offensiveBonus(level);
}

/** Special effect value (dot/drain/execute): +1/level. Shield: +1 per SHIELD_LEVELS_PER_BONUS levels. Returns base value unchanged for other non-scalable effects. */
export function calculateScaledSpecialValue(combat: SpeciesCombatConfig, level: number): number {
  if (combat.magic.special === COMBAT_SPECIAL_EFFECT.Shield) {
    return calculateScaledShieldValue(combat.magic.specialValue ?? 0, level);
  }
  if (!hasScalableSpecialEffect(combat)) return combat.magic.specialValue ?? 0;
  return (combat.magic.specialValue ?? 0) + offensiveBonus(level);
}

/** Recover heal: base + floor(0.1 * (level - 1)) — minimal scaling to preserve recover balance. */
export function calculateScaledRecoverHeal(baseHeal: number, level: number): number {
  return baseHeal + Math.floor(0.1 * (level - 1));
}

/** Shield value (any skill — attack/magic/dodge): base + 1 per SHIELD_LEVELS_PER_BONUS levels. */
export function calculateScaledShieldValue(baseValue: number, level: number): number {
  return baseValue + shieldBonus(level);
}

/** Scales a skill's specialValue if (and only if) that skill's special is Shield. Other skills pass through unchanged. */
function scaleSkillShieldValue(skill: CombatSkill, level: number): number | undefined {
  if (skill.special !== COMBAT_SPECIAL_EFFECT.Shield) return skill.specialValue;
  return calculateScaledShieldValue(skill.specialValue ?? 0, level);
}

/** Magic heal (e.g. Pinky's Wishing Bubble): base + offensive bonus (+1/level) */
export function calculateScaledMagicHeal(baseHeal: number, level: number): number {
  if (baseHeal <= 0) return 0;
  return baseHeal + offensiveBonus(level);
}

// ============================================================================
// Level-Gap Damage Multiplier (for cross-level advantage)
// ============================================================================

/**
 * Returns the damage multiplier for a combatant based on the level gap.
 * Only applies when the absolute gap exceeds LEVEL_GAP_THRESHOLD.
 * Same level or small gap → 1.0. Larger gap → scales from the threshold edge.
 * Used by both the battle engine (actual damage) and UI (displayed damage).
 */
export function calculateLevelGapMultiplier(ownLevel: number, opponentLevel: number): number {
  const gap = ownLevel - opponentLevel;
  const absGap = Math.abs(gap);
  if (absGap <= LEVEL_GAP_THRESHOLD) return 1;
  const effectiveGap = (absGap - LEVEL_GAP_THRESHOLD) * Math.sign(gap);
  return 1 + effectiveGap * LEVEL_GAP_DAMAGE_K;
}

// ============================================================================
// Primary API: Scale an entire combat config for a given level
// ============================================================================

/**
 * Returns a new SpeciesCombatConfig with all stat values scaled for the given level.
 *
 * This is the SINGLE ENTRY POINT for level scaling. Call it once at battle creation,
 * then pass the result to the battle engine. The engine reads .attack.damage, .magic.damage,
 * .recover.heal, .magic.specialValue etc. — all already scaled.
 *
 * Scaling rates:
 *   - baseHp:              +HP_PER_LEVEL/level (defensive)
 *   - attack.damage:       +floor(0.5/level) (offensive)
 *   - recover.heal:        +floor(0.1/level)
 *   - magic.damage:        +floor(0.5/level) if no scalable special, else +0
 *   - magic.heal:          +floor(0.5/level) (offensive)
 *   - magic.specialValue:  +floor(0.5/level) if scalable special (dot/drain/execute), else unchanged
 *   - dodge, chances, flags: unchanged
 */
export function scaleCombatConfig(combat: SpeciesCombatConfig, level: number): SpeciesCombatConfig {
  const scaleSkill = (skill: CombatSkill, damage: number, heal: number): CombatSkill => ({
    ...skill,
    damage,
    heal,
  });

  const isShieldOrScalable = (skill: CombatSkill) =>
    skill.special === COMBAT_SPECIAL_EFFECT.Shield || hasScalableSpecialEffect(combat);

  const scaledMagic: CombatSkill = {
    ...combat.magic,
    damage: calculateScaledMagicDamage(combat, level),
    heal: calculateScaledMagicHeal(combat.magic.heal, level),
    specialValue: isShieldOrScalable(combat.magic)
      ? calculateScaledSpecialValue(combat, level)
      : combat.magic.specialValue,
  };

  return {
    ...combat,
    baseHp: calculateScaledHp(combat.baseHp, level),
    attack: {
      ...scaleSkill(combat.attack, calculateScaledAttackDamage(combat.attack.damage, level), combat.attack.heal),
      specialValue: scaleSkillShieldValue(combat.attack, level),
    },
    recover: scaleSkill(combat.recover, combat.recover.damage, calculateScaledRecoverHeal(combat.recover.heal, level)),
    magic: scaledMagic,
    dodge: {
      ...combat.dodge,
      specialValue: scaleSkillShieldValue(combat.dodge, level),
    },
    // recoverUsesByStage, tuning, preciseAttack: unchanged
  };
}

// ============================================================================
// UI Summary (for stat display, tooltips, Help page)
// ============================================================================

/** Scaling rate constants for documentation and tests. */
export const LEVEL_SCALING = {
  HP_PER_LEVEL,
  ATTACK_DAMAGE_PER_LEVEL: 1,
  MAGIC_DAMAGE_PER_LEVEL: 1,
  SPECIAL_EFFECT_PER_LEVEL: 1,
  RECOVER_HEAL_PER_LEVEL: 0.1,
  SHIELD_VALUE_PER_LEVELS: SHIELD_LEVELS_PER_BONUS,
} as const;

/** Complete scaled stats for UI display. */
export interface ScaledCombatStats {
  hp: number;
  attackDamage: number;
  magicDamage: number;
  magicHeal: number;
  specialValue: number;
  recoverHeal: number;
  magicScales: boolean;
  specialScales: boolean;
  specialEffectType: ActiveCombatSpecialEffect | null;
}

/** Calculate all scaled stats for display purposes. Uses same formulas as scaleCombatConfig. */
export function calculateAllScaledStats(combat: SpeciesCombatConfig, level: number): ScaledCombatStats {
  return {
    hp: calculateScaledHp(combat.baseHp, level),
    attackDamage: calculateScaledAttackDamage(combat.attack.damage, level),
    magicDamage: calculateScaledMagicDamage(combat, level),
    magicHeal: calculateScaledMagicHeal(combat.magic.heal, level),
    specialValue: calculateScaledSpecialValue(combat, level),
    recoverHeal: calculateScaledRecoverHeal(combat.recover.heal, level),
    magicScales: !hasScalableSpecialEffect(combat),
    specialScales: hasScalableSpecialEffect(combat) || combat.magic.special === COMBAT_SPECIAL_EFFECT.Shield,
    specialEffectType: hasCombatSpecial(combat.magic.special) ? combat.magic.special : null,
  };
}
